import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, BarChart3, Users, FileAudio, Film, Radio, DollarSign, AlertCircle, TrendingUp, Shield, ArrowLeft, Flag, MessageCircle, Bell, X, Clapperboard, Send, Compass, CheckCircle, RefreshCw, Lock, UserX, Globe, Award, Briefcase, Brain, Plug, Trash2, Euro, Store, Pencil, Loader2, Code, Cpu, Wifi, Sparkles, Languages, Camera, Star, Share2, ScrollText, Video, Bot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ManageUsersModal from '../components/ManageUsersModal';
import AddCinemaContentModal from '../components/AddCinemaContentModal';
import AdminFranchisesPanel from '../components/AdminFranchisesPanel';
import ModerationPanel from '../components/ModerationPanel';
import StaffChat from '../components/StaffChat';
import Navigation from '../components/Navigation';
import AdminMonetizationModal from '../components/AdminMonetizationModal';
import AdminMyMouvPanel from '../components/AdminMyMouvPanel';
import AdminApiaPanel from '../components/AdminApiaPanel';
import AdminTeamPanel from '../components/AdminTeamPanel';
import AdminSecurityPanel from '../components/AdminSecurityPanel';
import AdminRegionalPanel from '../components/AdminRegionalPanel';
import DestineIAAvatar from '../components/admin/DestineIAAvatar';
import DeesseEmotionalPanel from '../components/admin/DeesseEmotionalPanel';
import BookScanner from '../components/admin/BookScanner';
import FilmsLibresToggle from '../components/admin/FilmsLibresToggle';
import SectionsVisibilityModal from '../components/admin/SectionsVisibilityModal';
import { CinemaRatingsPanel } from '../components/admin/AdminExtPanels';
import { GreenIARegistryAdminPanel, SubscriptionsAdminPanel } from '../components/admin/AdminV509Panels';
import AdsPremiumAdminPanel from '../components/admin/AdsPremiumAdminPanel';
import GreenIADivestPanel from '../components/admin/GreenIADivestPanel';
import PhotosAdminModal from '../components/admin/PhotosAdminModal';
import AdminEmailModal from '../components/admin/AdminEmailModal';
import BusinessTasksPanel from '../components/admin/BusinessTasksPanel';
import CyberdefensePanel from '../components/admin/CyberdefensePanel';
import SocialAIAgentsPanel from '../components/admin/SocialAIAgentsPanel';
import AdminVideoStudio from '../components/admin/AdminVideoStudio';
import SocialConnectPublish from '../components/admin/SocialConnectPublish';
import { useAdminLang } from '../hooks/useAdminLang';

const ADMIN_LANG_LABEL = { fr: 'Français', en: 'English', es: 'Español', zh: '中文' };

const AdminLangSwitcher = () => {
  const { adminLang, setAdminLang, supported } = useAdminLang();
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="admin-lang-switcher">
      <Globe className="w-4 h-4 text-white/50" />
      {supported.map(code => (
        <button
          key={code}
          onClick={() => setAdminLang(code)}
          data-testid={`admin-lang-${code}`}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            adminLang === code
              ? 'bg-fuchsia-500 border-fuchsia-400 text-white'
              : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
          }`}
        >
          {ADMIN_LANG_LABEL[code]}
        </button>
      ))}
    </div>
  );
};

const Admin = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.email === 'contact.destinestudio@gmail.com';
  const isManager = user?.admin_role === 'manager';
  const isSuperOrManager = isSuperAdmin || isManager;
  const isPlainAdmin = !!user?.is_admin && !isSuperOrManager;
  const [mySections, setMySections] = useState([]);
  useEffect(() => {
    if (isPlainAdmin) {
      axios.get('/api/admin/team/my-sections').then(r => setMySections(r.data.sections || [])).catch(() => {});
    }
  }, [isPlainAdmin]);
  const canSee = (id) => isSuperOrManager || mySections.includes(id);
  const navigate = useNavigate();
  const { at } = useAdminLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPhotosAdmin, setShowPhotosAdmin] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCinemaModal, setShowCinemaModal] = useState(false);
  const [showFranchisesModal, setShowFranchisesModal] = useState(false);
  const [showStaffChat, setShowStaffChat] = useState(false);
  const [showUpdateNotificationModal, setShowUpdateNotificationModal] = useState(false);
  const [showWeeklyCinemaModal, setShowWeeklyCinemaModal] = useState(false);
  const [showFreeFilmModal, setShowFreeFilmModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showMediaBadgeModal, setShowMediaBadgeModal] = useState(false);
  const [showBadgeCandidatesModal, setShowBadgeCandidatesModal] = useState(false);
  const [showAIInsightsModal, setShowAIInsightsModal] = useState(false);
  const [showMonetizationModal, setShowMonetizationModal] = useState(false);
  const [showApiIntegrationsModal, setShowApiIntegrationsModal] = useState(false);
  const [showUsersByLangModal, setShowUsersByLangModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blockNotifications, setBlockNotifications] = useState([]);
  const [unreadBlockCount, setUnreadBlockCount] = useState(0);
  const [showBlockNotifications, setShowBlockNotifications] = useState(false);
  const [expiredAds, setExpiredAds] = useState([]);
  const [staffUnreadCount, setStaffUnreadCount] = useState(0);
  const [pinVerified, setPinVerified] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinCodes, setPinCodes] = useState(null);
  const [showPinCodes, setShowPinCodes] = useState(false);
  const [showEditPin, setShowEditPin] = useState(false);

  // Update date every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Vérifier si admin
    if (!user?.is_admin) {
      navigate('/feed');
      return;
    }
    // Check if PIN was already verified this session
    const verified = sessionStorage.getItem('admin_pin_verified');
    if (verified === user?.id) {
      setPinVerified(true);
      loadStats();
      loadBlockNotifications();
      loadExpiredAds();
      loadStaffUnreadCount();
    } else {
      // PIN not verified yet, stop loading to show PIN entry screen
      setLoading(false);
    }
  }, [user, navigate]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pinInput.length !== 4) {
      setPinError('Le code doit contenir 4 chiffres');
      return;
    }
    setPinLoading(true);
    setPinError('');
    try {
      await axios.post('/api/admin/verify-pin', { pin: pinInput });
      setPinVerified(true);
      sessionStorage.setItem('admin_pin_verified', user?.id);
      loadStats();
      loadBlockNotifications();
      loadExpiredAds();
      loadStaffUnreadCount();
    } catch (error) {
      setPinError(error.response?.data?.detail || 'Code incorrect');
      setPinInput('');
    } finally {
      setPinLoading(false);
    }
  };

  const loadPinCodes = async () => {
    try {
      const res = await axios.get('/api/admin/pin-codes');
      setPinCodes(res.data);
      setShowPinCodes(true);
    } catch (error) {
      toast.error('Accès réservé au super admin');
    }
  };

  const loadStaffUnreadCount = async () => {
    try {
      const res = await axios.get('/api/staff-chat/unread-count');
      setStaffUnreadCount(res.data?.unread_count || 0);
    } catch (e) {}
  };

  const loadExpiredAds = async () => {
    try {
      const res = await axios.get('/api/admin/ads/expired');
      setExpiredAds(res.data || []);
    } catch (error) {
      console.error('Error loading expired ads:', error);
    }
  };

  const handleDeleteExpiredAd = async (adId) => {
    try {
      await axios.delete(`/api/admin/content/ads/${adId}`);
      toast.success('Publicité supprimée');
      setExpiredAds(prev => prev.filter(a => a.id !== adId));
      loadStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const loadBlockNotifications = async () => {
    try {
      const [notificationsRes, countRes] = await Promise.all([
        axios.get('/api/admin/block-notifications'),
        axios.get('/api/admin/block-notifications/unread-count')
      ]);
      setBlockNotifications(notificationsRes.data);
      setUnreadBlockCount(countRes.data.count);
    } catch (error) {
      console.error('Error loading block notifications:', error);
    }
  };

  const markBlockNotificationsAsRead = async () => {
    try {
      await axios.post('/api/notifications/read-all');
      setUnreadBlockCount(0);
      loadBlockNotifications();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Charger les statistiques depuis différentes collections
      const [usersRes, postsRes, podcastsRes, adsRes, photosRes, videosRes] = await Promise.all([
        axios.get('/api/admin/users-count').catch(() => ({ data: { count: 0 } })),
        axios.get('/api/admin/posts-count').catch(() => ({ data: { count: 0 } })),
        axios.get('/api/admin/podcasts-count').catch(() => ({ data: { count: 0 } })),
        axios.get('/api/admin/ads-count').catch(() => ({ data: { count: 0 } })),
        axios.get('/api/admin/photos-count').catch(() => ({ data: { count: 0 } })),
        axios.get('/api/admin/videos-count').catch(() => ({ data: { count: 0 } }))
      ]);

      setStats({
        users: usersRes.data.count,
        posts: postsRes.data.count,
        podcasts: podcastsRes.data.count,
        ads: adsRes.data.count,
        photos: photosRes.data.count,
        videos: videosRes.data.count
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      // Données de fallback
      setStats({
        users: 0,
        posts: 0,
        podcasts: 0,
        ads: 0,
        photos: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!pinVerified) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Espace Admin</h1>
            <p className="text-gray-400 text-sm">Entrez votre code d'accès à 4 chiffres</p>
          </div>
          
          {/* PIN dots */}
          <div className="flex justify-center gap-3 mb-6">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                  pinInput.length > i 
                    ? 'border-purple-500 bg-purple-500/20' 
                    : 'border-white/20 bg-white/5'
                }`}
              >
                {pinInput[i] ? '\u2022' : ''}
              </div>
            ))}
          </div>
          
          {/* Custom numpad only - no hidden input to avoid iOS keyboard */}
          <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto mb-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'].map((num, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  if (num === 'del') {
                    setPinInput(prev => prev.slice(0, -1));
                    setPinError('');
                  } else if (num !== null && pinInput.length < 4) {
                    const newPin = pinInput + num;
                    setPinInput(newPin);
                    setPinError('');
                    // Auto-submit when 4 digits entered
                    if (newPin.length === 4) {
                      setPinLoading(true);
                      axios.post('/api/admin/verify-pin', { pin: newPin })
                        .then(() => {
                          setPinVerified(true);
                          sessionStorage.setItem('admin_pin_verified', user?.id);
                          loadStats();
                          loadBlockNotifications();
                          loadExpiredAds();
                          loadStaffUnreadCount();
                        })
                        .catch(err => {
                          setPinError(err.response?.data?.detail || 'Code incorrect');
                          setPinInput('');
                        })
                        .finally(() => setPinLoading(false));
                    }
                  }
                }}
                disabled={pinLoading}
                className={`h-14 rounded-xl font-bold text-xl transition-all ${
                  num === null 
                    ? 'invisible' 
                    : num === 'del'
                    ? 'bg-white/5 hover:bg-red-500/20 text-gray-400 text-base'
                    : 'bg-white/5 hover:bg-white/10 active:bg-purple-500/30'
                }`}
              >
                {num === 'del' ? '\u232b' : num !== null ? num : ''}
              </button>
            ))}
          </div>

          {pinError && (
            <p className="text-red-400 text-sm text-center mb-4">{pinError}</p>
          )}

          {pinLoading && (
            <div className="flex justify-center mb-4">
              <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          
          <button
            onClick={() => navigate('/feed')}
            className="w-full py-3 text-gray-400 hover:text-white text-sm transition-colors"
          >
            Retour
          </button>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <div className="container mx-auto px-4 py-6">
        {/* Exit Button + Admin language switcher */}
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate('/feed')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{at('exit')}</span>
          </button>
          <AdminLangSwitcher />
        </div>

        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
              {at('dashboardTitle')}
            </h1>
            <p className="text-gray-400">{at('dashboardSubtitle')}</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Block Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowBlockNotifications(!showBlockNotifications);
                  if (!showBlockNotifications) {
                    markBlockNotificationsAsRead();
                  }
                }}
                className="relative p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <Bell className="w-6 h-6 text-white" />
                {unreadBlockCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
                    {unreadBlockCount}
                  </span>
                )}
              </button>
              
              {/* Notifications Dropdown */}
              {showBlockNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-gray-900 border border-white/10 rounded-xl shadow-2xl z-50 max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <UserX className="w-5 h-5 text-red-400" />
                      <h3 className="font-semibold">Alertes de blocage</h3>
                    </div>
                    <div className="flex items-center gap-1">
                      {blockNotifications.length > 0 && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Supprimer toutes les alertes ?')) return;
                            try {
                              await axios.delete('/api/admin/block-notifications');
                              setBlockNotifications([]);
                              // Optimistically clear the unread badge (state may not exist)
                              try { /* no-op */ } catch (_) {}
                            } catch (e) { toast.error('Suppression échouée'); }
                          }}
                          className="p-1.5 hover:bg-white/10 rounded-full text-red-400"
                          title="Tout supprimer"
                          data-testid="admin-delete-all-block-notifs"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setShowBlockNotifications(false)}
                        className="text-gray-400 hover:text-white p-1"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {blockNotifications.length === 0 ? (
                    <div className="p-4 text-center text-gray-400">
                      Aucune alerte de blocage
                    </div>
                  ) : (
                    <div className="divide-y divide-white/10">
                      {blockNotifications.map(notif => (
                        <div key={notif.id} className="p-3 hover:bg-white/5 flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">{notif.content}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(notif.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                await axios.delete(`/api/admin/block-notifications/${notif.id}`);
                                setBlockNotifications(prev => prev.filter(n => n.id !== notif.id));
                              } catch (e) { toast.error('Suppression échouée'); }
                            }}
                            className="p-1 hover:bg-white/10 rounded-full text-red-400 shrink-0"
                            title="Supprimer"
                            data-testid={`admin-delete-block-notif-${notif.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="text-right">
              <div className="text-2xl font-bold text-white">
                {currentDate.toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="text-gray-400 text-sm">
                {currentDate.toLocaleTimeString('fr-FR', { 
                  hour: '2-digit', 
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Super Admin - PIN Codes Section */}
        {isSuperAdmin && (
          <div className="mb-6 space-y-4">
            <button
              onClick={() => showPinCodes ? setShowPinCodes(false) : loadPinCodes()}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors"
              data-testid="show-pin-codes-btn"
            >
              <Lock className="w-4 h-4 text-purple-400" />
              <span>{showPinCodes ? at('pinHide') : at('pinShow')}</span>
            </button>
            {showPinCodes && pinCodes && (
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-purple-300">{at('pinTitle')}</h3>
                  <button
                    onClick={() => setShowEditPin(true)}
                    className="px-3 py-1 rounded-full bg-purple-600 hover:bg-purple-700 text-[11px] font-semibold flex items-center gap-1"
                    data-testid="edit-pin-codes-btn"
                  >
                    <Pencil className="w-3 h-3" /> {at('pinEdit')}
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-2.5">
                    <span className="text-sm text-gray-300">{at('pinSuper')}</span>
                    <span className="font-mono font-bold text-lg text-purple-400 tracking-widest">{pinCodes.super_admin_pin}</span>
                  </div>
                  <div className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-2.5">
                    <span className="text-sm text-gray-300">{at('pinOthers')}</span>
                    <span className="font-mono font-bold text-lg text-pink-400 tracking-widest">{pinCodes.admin_pin}</span>
                  </div>
                </div>
              </div>
            )}

            {/* APIs Section */}
            <ApiKeysSection />

            {/* Destine IA architecture (super-admin only, gated by /api/admin/destine-ia-architecture) */}
            <DestineIaArchitectureSection />

            {/* Destine AP-IA — API keys for external integrators */}
            <DestineApiaKeysSection />

            {/* Team (Admins & Managers) — super-admin only */}
            <DestineTeamSection />

            {/* Inactive Users */}
            <InactiveUsersSection />
          </div>
        )}

        {/* Security & Regional-language corpus — super-admin + managers */}
        {isSuperOrManager && (
          <div className="mb-6 space-y-4">
            <DestineSecuritySection />
            <DestineRegionalSection />
            <DestineAfricaSection />
            <GreenIAAdminSection />
            <SubscriptionsAdminSection />
            <AdsPremiumAdminSection />
            <BusinessTasksSection />
            <CinemaRatingsAdminSection />
            <SocialAgentsAdminSection />
          </div>
        )}

        {/* Edit PIN modal — super-admin only */}
        {showPhotosAdmin && (
          <PhotosAdminModal onClose={() => setShowPhotosAdmin(false)} />
        )}
        {showEmailModal && (
          <AdminEmailModal onClose={() => setShowEmailModal(false)} />
        )}
        {showEditPin && (
          <EditPinCodesModal
            current={pinCodes}
            onClose={() => setShowEditPin(false)}
            onSaved={(next) => { setPinCodes(next); setShowEditPin(false); toast.success('Codes PIN mis à jour'); }}
          />
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatCard
            icon={<Users className="w-8 h-8" />}
            title={at('tileUsersTitle') || 'Utilisateurs'}
            value={stats?.users || 0}
            trend="+12%"
            color="from-blue-500 to-cyan-500"
            onClick={() => setShowUsersByLangModal(true)}
            testid="admin-stat-users-btn"
          />
          <StatCard
            icon={<FileAudio className="w-8 h-8" />}
            title="Gim Toli Posts"
            value={stats?.posts || 0}
            trend="+8%"
            color="from-magenta-500 to-pink-500"
          />
          <StatCard
            icon={<Radio className="w-8 h-8" />}
            title="Podcasts"
            value={stats?.podcasts || 0}
            trend="+5%"
            color="from-violet-500 to-purple-500"
          />
          <StatCard
            icon={<Camera className="w-8 h-8" />}
            title={at('photosTitle') || 'Photos partagées'}
            value={stats?.photos || 0}
            trend="+10%"
            color="from-pink-500 to-fuchsia-500"
            onClick={() => setShowPhotosAdmin(true)}
            testid="admin-stat-photos-btn"
          />
          <StatCard
            icon={<Video className="w-8 h-8" />}
            title={at('videosTitle') || 'Vidéos partagées'}
            value={stats?.videos || 0}
            trend="+10%"
            color="from-cyan-500 to-sky-600"
            testid="admin-stat-videos"
          />
          <StatCard
            icon={<DollarSign className="w-8 h-8" />}
            title="Publicités"
            value={stats?.ads || 0}
            trend="+15%"
            color="from-green-500 to-emerald-500"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Actions Rapides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {canSee('update_notif') && <ActionCard
              icon={<Send className="w-6 h-6" />}
              title={at('tileUpdateNotifTitle')}
              description={at('tileUpdateNotifDesc')}
              onClick={() => setShowUpdateNotificationModal(true)}
              highlight={true}
            />}
            {canSee('weekly_cinema') && <ActionCard
              icon={<Send className="w-6 h-6" />}
              title={at('tileWeeklyCinemaTitle')}
              description={at('tileWeeklyCinemaDesc')}
              onClick={() => setShowWeeklyCinemaModal(true)}
              data-testid="admin-weekly-cinema-action"
              highlight={true}
            />}
            {canSee('admin_email') && <ActionCard
              icon={<Send className="w-6 h-6" />}
              title={at('tileEmailTitle')}
              description={at('tileEmailDesc')}
              onClick={() => setShowEmailModal(true)}
              highlight={true}
              data-testid="admin-email-action"
            />}
            {canSee('users') && <ActionCard
              icon={<Users className="w-6 h-6" />}
              title={at('tileUsersTitle')}
              description={at('tileUsersDesc')}
              onClick={() => setShowUsersModal(true)}
            />}
            {canSee('badges') && <ActionCard
              icon={<CheckCircle className="w-6 h-6" />}
              title={at('tileBadgeTitle')}
              description={at('tileBadgeDesc')}
              onClick={() => setShowMediaBadgeModal(true)}
            />}
            {canSee('badge_candidates') && <ActionCard
              icon={<Award className="w-6 h-6" />}
              title={at('tileBadgeCandidatesTitle')}
              description={at('tileBadgeCandidatesDesc')}
              onClick={() => setShowBadgeCandidatesModal(true)}
            />}
            {canSee('myia_insights') && <ActionCard
              icon={<Brain className="w-6 h-6" />}
              title={at('tileMyIaInsightsTitle')}
              description={at('tileMyIaInsightsDesc')}
              onClick={() => setShowAIInsightsModal(true)}
            />}
            {isSuperAdmin && (
              <ActionCard
                icon={<Plug className="w-6 h-6" />}
                title={at('tileApiIntegrationsTitle')}
                description={at('tileApiIntegrationsDesc')}
                onClick={() => setShowApiIntegrationsModal(true)}
              />
            )}
            {canSee('moderation') && <ActionCard
              icon={<Shield className="w-6 h-6" />}
              title={at('tileModerationTitle')}
              description={at('tileModerationDesc')}
              onClick={() => navigate('/admin/moderation')}
            />}
            {canSee('cinema') && <ActionCard
              icon={<Clapperboard className="w-6 h-6" />}
              title={at('tileCinemaTitle')}
              description={at('tileCinemaDesc')}
              onClick={() => setShowCinemaModal(true)}
            />}
            {canSee('franchises') && <ActionCard
              icon={<Sparkles className="w-6 h-6" />}
              title={at('tileFranchisesTitle')}
              description={at('tileFranchisesDesc')}
              onClick={() => setShowFranchisesModal(true)}
              data-testid="admin-franchises-tile"
            />}
            {canSee('tmdb_sync') && <ActionCard
              icon={<RefreshCw className="w-6 h-6" />}
              title={at('tileTmdbTitle')}
              description={at('tileTmdbDesc')}
              onClick={async () => {
                toast.loading(at('tmdbSyncing'), { id: 'tmdb-sync' });
                try {
                  const res = await axios.post('/api/tmdb/sync');
                  const { upcoming_movies, box_office } = res.data;
                  toast.success(
                    `${at('tmdbSyncDone')} — Films: +${upcoming_movies?.added || 0}, Box Office: +${box_office?.added || 0}`,
                    { id: 'tmdb-sync', duration: 5000 }
                  );
                } catch (error) {
                  toast.error(at('tmdbSyncError'), { id: 'tmdb-sync' });
                }
              }}
            />}
            {canSee('free_films') && <ActionCard
              icon={<Compass className="w-6 h-6" />}
              title={at('tileFreeFilmTitle')}
              description={at('tileFreeFilmDesc')}
              onClick={() => setShowFreeFilmModal(true)}
            />}
            {canSee('free_films') && <ActionCard
              icon={<Eye className="w-6 h-6" />}
              title={at('tileVisibilityTitle')}
              description={at('tileVisibilityDesc')}
              onClick={() => setShowVisibilityModal(true)}
            />}
            {canSee('ads_validation') && <ActionCard
              icon={<DollarSign className="w-6 h-6" />}
              title={at('tileAdsValidationTitle')}
              description={at('tileAdsValidationDesc')}
              onClick={() => navigate('/admin/ads-validation')}
            />}
            {canSee('monetization') && <ActionCard
              icon={<DollarSign className="w-6 h-6" />}
              title={at('tileMonetizationTitle')}
              description={at('tileMonetizationDesc')}
              onClick={() => setShowMonetizationModal(true)}
              data-testid="open-monetization-admin"
            />}
            {canSee('ads_pro') && <ActionCard
              icon={<TrendingUp className="w-6 h-6" />}
              title={at('tileAdsTitle')}
              description={at('tileAdsDesc')}
              onClick={() => navigate('/pro-space')}
            />}
            {canSee('staff_chat') && <ActionCard
              icon={
                <div className="relative">
                  <MessageCircle className="w-6 h-6" />
                  {staffUnreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {staffUnreadCount > 9 ? '9+' : staffUnreadCount}
                    </span>
                  )}
                </div>
              }
              title="Chat du Personnel"
              description="Discuter avec l'équipe admin"
              onClick={() => { setShowStaffChat(true); setStaffUnreadCount(0); }}
            />}
            {canSee('analytics') && <ActionCard
              icon={<BarChart3 className="w-6 h-6" />}
              title="Analytiques"
              description="Statistiques détaillées"
              onClick={() => navigate('/admin/analytics')}
            />}
            {canSee('trends') && <ActionCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Tendances"
              description="Contenus populaires"
              onClick={() => navigate('/admin/trends')}
            />}
            {canSee('reports') && <ActionCard
              icon={<Flag className="w-6 h-6" />}
              title="Signalements"
              description="Gérer les signalements"
              onClick={() => navigate('/admin/reports')}
            />}
            {canSee('group_reports') && <ActionCard
              icon={<Flag className="w-6 h-6" />}
              title="Signalements Groupes"
              description="Groupes & messages signalés"
              onClick={() => navigate('/admin/group-reports')}
            />}
            {canSee('accounting') && <ActionCard
              icon={<Euro className="w-6 h-6" />}
              title="Comptabilité"
              description="Revenus Publicités + e-Dépôts"
              onClick={() => navigate('/admin/accounting')}
              data-testid="admin-accounting-tile"
            />}
            {canSee('myshop') && <ActionCard
              icon={<Store className="w-6 h-6" />}
              title="MyShop"
              description="Boutiques, RIB, réclamations"
              onClick={() => navigate('/admin/shops')}
              data-testid="admin-shops-tile"
            />}
            {canSee('esim') && <ActionCard
              icon={<Wifi className="w-6 h-6" />}
              title="eSIM (Airalo)"
              description="Commandes eSIM, KPI, support"
              onClick={() => navigate('/admin/esims')}
              data-testid="admin-esims-tile"
            />}
            {canSee('myjob') && <ActionCard
              icon={<Briefcase className="w-6 h-6" />}
              title="MyJob — Offres"
              description="Offres, revenus (net after Stripe fees)"
              onClick={() => navigate('/admin/myjob')}
              data-testid="admin-myjob-tile"
            />}
            {canSee('legal_docs') && <ActionCard
              icon={<ScrollText className="w-6 h-6" />}
              title={at('legalDocsTileTitle') || 'Documents juridiques'}
              description={at('legalDocsTileDesc') || 'CGU, CGV, confidentialité, PDF & éditeur'}
              onClick={() => navigate('/admin/legal-docs')}
              data-testid="admin-legal-docs-tile"
            />}
            {canSee('edepots') && <ActionCard
              icon={<Shield className="w-6 h-6" />}
              title={at('edepotsTileTitle') || 'e-Dépôts'}
              description={at('edepotsTileDesc') || 'Attestations, téléchargements, dépôt test gratuit'}
              onClick={() => navigate('/admin/edepots')}
              data-testid="admin-edepots-tile"
            />}
          </div>
        </div>

        {/* MyMouv (Events & tickets) */}
        {canSee('mymouv_events') && (
        <div className="bg-white/5 rounded-2xl p-6 border border-fuchsia-500/20">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-400" />
            MyMouv — Événements &amp; billetterie
          </h2>
          <AdminMyMouvPanel />
        </div>
        )}

        {/* Expired Ads Alert */}
        {expiredAds.length > 0 && (
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <h2 className="text-lg font-bold text-red-400">
                {expiredAds.length} publicité{expiredAds.length > 1 ? 's' : ''} expirée{expiredAds.length > 1 ? 's' : ''}
              </h2>
            </div>
            <div className="space-y-3">
              {expiredAds.map(ad => (
                <div key={ad.id} className="flex items-center justify-between bg-black/20 rounded-xl p-3">
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {ad.ad_type === 'video' ? 'Vidéo' : ad.ad_type === 'audio' ? 'Audio' : 'Image'} — {ad.user?.name || 'Inconnu'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {ad.impressions_count || 0} vues • {ad.clicks_count || 0} clics • Expirée le {new Date(ad.expires_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteExpiredAd(ad.id)}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-colors"
                    data-testid={`delete-expired-ad-${ad.id}`}
                  >
                    Supprimer
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leaderboard */}
        <LeaderboardSection />

        {/* Anti-abuse dashboard */}
        <AntiAbuseSection />
      </div>

      {/* Modals */}
      {showUsersModal && (
        <ManageUsersModal onClose={() => setShowUsersModal(false)} />
      )}

      {showCinemaModal && (
        <AddCinemaContentModal 
          onClose={() => setShowCinemaModal(false)} 
          onSuccess={() => {
            setShowCinemaModal(false);
            loadStats();
          }}
        />
      )}

      {showFranchisesModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Franchises, Studios & Streaming</h2>
              <button onClick={() => setShowFranchisesModal(false)} className="p-2 rounded hover:bg-white/10 text-white">✕</button>
            </div>
            <AdminFranchisesPanel />
          </div>
        </div>
      )}

      {showStaffChat && (
        <StaffChat onClose={() => setShowStaffChat(false)} />
      )}

      {showUpdateNotificationModal && (
        <UpdateNotificationModal 
          onClose={() => setShowUpdateNotificationModal(false)}
        />
      )}

      {showWeeklyCinemaModal && (
        <WeeklyCinemaDigestModal
          onClose={() => setShowWeeklyCinemaModal(false)}
        />
      )}

      {showVisibilityModal && <SectionsVisibilityModal onClose={() => setShowVisibilityModal(false)} />}
      {showFreeFilmModal && (
        <AddFreeFilmModal 
          onClose={() => setShowFreeFilmModal(false)}
          onSuccess={() => {
            setShowFreeFilmModal(false);
            toast.success('Film ajouté!');
          }}
        />
      )}

      {showMediaBadgeModal && (
        <MediaBadgeModal 
          onClose={() => setShowMediaBadgeModal(false)}
        />
      )}

      {showBadgeCandidatesModal && (
        <BadgeCandidatesModal
          onClose={() => setShowBadgeCandidatesModal(false)}
        />
      )}

      {showAIInsightsModal && (
        <AIInsightsModal
          onClose={() => setShowAIInsightsModal(false)}
        />
      )}

      {showApiIntegrationsModal && (
        <ApiIntegrationsModal
          onClose={() => setShowApiIntegrationsModal(false)}
        />
      )}

      {showMonetizationModal && (
        <AdminMonetizationModal onClose={() => setShowMonetizationModal(false)} />
      )}

      {showUsersByLangModal && (
        <UsersByLanguageModal onClose={() => setShowUsersByLangModal(false)} />
      )}
    </div>
  );
};

// Update Notification Modal
const UpdateNotificationModal = ({ onClose }) => {
  const { at } = useAdminLang();
  const [message, setMessage] = useState('Une nouvelle version de Destine est disponible sur l\'App Store. Mettez à jour pour profiter des dernières fonctionnalités!');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await axios.post('/api/push/admin/send-update-notification', { message });
      toast.success(`Notification envoyée à ${response.data.notifications_sent} utilisateurs!`);
      onClose();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{at('tileUpdateNotifTitle')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-4">
          Cette notification sera envoyée à tous les utilisateurs dans la cloche de notifications et en push sur iOS.
        </p>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-green-500 min-h-[100px] mb-4"
          placeholder="Message de notification..."
        />

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold"
          >
            Annuler
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-full font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Envoi...' : 'Envoyer à tous'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Weekly Cinema Digest Modal (admin manual trigger)
const WeeklyCinemaDigestModal = ({ onClose }) => {
  const { at } = useAdminLang();
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post('/api/cron/weekly-cinema-digest?dry_run=true');
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setPreviewing(false); }
  };

  const runSend = async () => {
    if (!window.confirm(at('weeklyCinemaConfirm'))) return;
    setSending(true);
    try {
      const res = await axios.post('/api/cron/weekly-cinema-digest?dry_run=false');
      toast.success(`${res.data.emails_sent || 0} emails · ${res.data.notifications || 0} notifs (${res.data.week || ''})`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{at('tileWeeklyCinemaTitle')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-4">{at('weeklyCinemaHint')}</p>

        {preview && (
          <div className="mb-4 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-sm">
            <div className="font-semibold text-cyan-300 mb-1">{at('weeklyCinemaAudience')} : {preview.audience || 0}</div>
            <div className="text-xs text-gray-400">{at('weeklyCinemaFilms')} :</div>
            <ul className="text-xs list-disc pl-5 mt-1 space-y-0.5">
              {(preview.films || []).map((title, i) => <li key={i}>{title}</li>)}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={runPreview}
            disabled={previewing || sending}
            className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold disabled:opacity-50"
            data-testid="weekly-cinema-preview-btn"
          >
            {previewing ? '…' : at('weeklyCinemaPreview')}
          </button>
          <button
            onClick={runSend}
            disabled={sending || previewing}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-full font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="weekly-cinema-send-btn"
          >
            <Send className="w-4 h-4" />
            {sending ? at('weeklyCinemaSending') : at('weeklyCinemaSend')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Free Film Modal
const AddFreeFilmModal = ({ onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [image, setImage] = useState(null);
  const [filmLanguage, setFilmLanguage] = useState('fr');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error('Titre et description requis');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('language', filmLanguage);
      if (externalUrl) formData.append('external_url', externalUrl);
      if (image) formData.append('image', image);

      await axios.post('/api/explorer/free-films', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSuccess();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="add-free-film-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Ajouter un film libre de droit</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description * (5 lignes max)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 min-h-[100px]"
              required
            />
            <p className="text-xs text-gray-500 mt-1">{description.length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Lien externe</label>
            <input
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImage(e.target.files[0])}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Langue du film</label>
            <select
              value={filmLanguage}
              onChange={(e) => setFilmLanguage(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-white"
            >
              {['fr','en','es','ar','pt','de','ru','zh','ja','hi','nl','sw'].map(l => (
                <option key={l} value={l} className="bg-gray-900">{l.toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-full font-semibold disabled:opacity-50"
            >
              {uploading ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Media Badge Modal
const MediaBadgeModal = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchUsers = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      const response = await axios.get(`/api/blocked-users/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const toggleBadge = async (userId, currentStatus) => {
    try {
      await axios.post(`/api/push/admin/verify-user/${userId}`, {
        is_verified: !currentStatus
      });
      toast.success(currentStatus ? 'Badge retiré' : 'Badge vérifié attribué');
      searchUsers(); // Refresh
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            Badge vérifié
            <svg viewBox="0 0 22 22" className="w-5 h-5"><path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.69-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.636.433 1.221.878 1.69.47.446 1.055.752 1.69.883.635.13 1.294.083 1.902-.141.27.587.7 1.086 1.24 1.44.54.354 1.167.551 1.813.568.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.223 1.26.27 1.894.14.634-.132 1.22-.438 1.69-.884.445-.47.75-1.055.88-1.69.13-.634.083-1.29-.14-1.898.587-.273 1.084-.704 1.438-1.244.355-.54.553-1.17.57-1.817z" fill="#1d9bf0"/><path d="M9.585 14.929l-3.28-3.28 1.168-1.168 2.112 2.112 5.06-5.06 1.168 1.168-6.228 6.228z" fill="white"/></svg>
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-4">
          Attribuez un badge vérifié bleu aux utilisateurs les plus méritants.
        </p>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un utilisateur..."
            className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={searchUsers}
            disabled={searching}
            className="px-4 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl font-semibold"
          >
            {searching ? '...' : 'Rechercher'}
          </button>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {searchResults.map(user => (
            <div key={user.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium flex items-center gap-1.5">
                    {user.name}
                    {user.is_verified && (
                      <svg viewBox="0 0 22 22" className="w-4 h-4"><path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.69-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.636.433 1.221.878 1.69.47.446 1.055.752 1.69.883.635.13 1.294.083 1.902-.141.27.587.7 1.086 1.24 1.44.54.354 1.167.551 1.813.568.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.223 1.26.27 1.894.14.634-.132 1.22-.438 1.69-.884.445-.47.75-1.055.88-1.69.13-.634.083-1.29-.14-1.898.587-.273 1.084-.704 1.438-1.244.355-.54.553-1.17.57-1.817z" fill="#1d9bf0"/><path d="M9.585 14.929l-3.28-3.28 1.168-1.168 2.112 2.112 5.06-5.06 1.168 1.168-6.228 6.228z" fill="white"/></svg>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">@{user.username}</p>
                </div>
              </div>
              <button
                onClick={() => toggleBadge(user.id, user.is_verified)}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  user.is_verified 
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                    : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                }`}
              >
                {user.is_verified ? 'Retirer' : 'Vérifier'}
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold"
        >
          Fermer
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, trend, color, onClick, testid }) => {
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 transition-all text-left w-full ${clickable ? 'hover:border-magenta-500/50 hover:bg-white/10 active:scale-[0.98] cursor-pointer' : 'hover:border-magenta-500/30'}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 bg-gradient-to-br ${color} rounded-xl`}>
          {icon}
        </div>
        <span className="text-green-500 text-sm font-semibold">{trend}</span>
      </div>
      <h3 className="text-gray-400 text-sm mb-1">{title}</h3>
      <p className="text-3xl font-bold">{value.toLocaleString()}</p>
    </Wrapper>
  );
};

const ApiKeysSection = () => {
  const [apis, setApis] = useState(null);
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();

  const load = async () => {
    try {
      const res = await axios.get('/api/admin/api-keys');
      setApis(res.data);
      setShow(true);
    } catch { setShow(false); }
  };

  return (
    <div>
      <button onClick={() => show ? setShow(false) : load()} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors">
        <Globe className="w-4 h-4 text-cyan-400" />
        <span>{show ? at('hideSection') : at('showApisUsed')}</span>
      </button>
      {show && apis && (
        <div className="mt-3 bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-cyan-300 mb-3">APIs & Services</h3>
          <div className="space-y-2">
            {apis.map((api, i) => (
              <div key={i} className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-2.5">
                <span className="text-sm text-gray-300">{api.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${api.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {api.status === 'active' ? 'Actif' : 'Manquant'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const InactiveUsersSection = () => {
  const [users, setUsers] = useState(null);
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();

  const load = async () => {
    try {
      const res = await axios.get('/api/admin/inactive-users');
      setUsers(res.data);
      setShow(true);
    } catch { setShow(false); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur définitivement ?')) return;
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div>
      <button onClick={() => show ? setShow(false) : load()} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors">
        <UserX className="w-4 h-4 text-orange-400" />
        <span>{show ? at('hideSection') : at('inactiveUsersSection')}</span>
      </button>
      {show && users && (
        <div className="mt-3 bg-orange-500/5 border border-orange-500/20 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-orange-300 mb-3">{at('inactiveUsersTitle')} ({users.length})</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {users.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun utilisateur inactif</p>
            ) : users.map(u => (
              <div key={u.id} className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-2.5">
                <div>
                  <span className="text-sm text-white">{u.name || u.email}</span>
                  <p className="text-xs text-gray-500">Dernière connexion: {u.last_seen ? new Date(u.last_seen).toLocaleDateString('fr-FR') : 'Jamais'}</p>
                </div>
                <button onClick={() => deleteUser(u.id)} className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs hover:bg-red-500/30">
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AdsHistorySection = () => {
  const [ads, setAds] = useState(null);
  const [show, setShow] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get('/api/admin/ads-history');
      setAds(res.data);
      setShow(true);
    } catch { setShow(false); }
  };

  return (
    <div>
      <button onClick={() => show ? setShow(false) : load()} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors">
        <TrendingUp className="w-4 h-4 text-violet-400" />
        <span>{show ? 'Masquer historique pubs' : 'Historique des publicités'}</span>
      </button>
      {show && ads && (
        <div className="mt-3 bg-violet-500/5 border border-violet-500/20 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-violet-300 mb-3">Historique ({ads.length})</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {ads.map(ad => (
              <div key={ad.id} className="bg-black/20 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">{ad.title || 'Sans titre'}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${ad.is_expired ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                    {ad.is_expired ? 'Expirée' : 'Active'}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span>Par: {ad.advertiser_name}</span>
                  <span>|</span>
                  <span>{ad.amount ? `${ad.amount}€` : 'Gratuit'}</span>
                  <span>|</span>
                  <span>{ad.created_at ? new Date(ad.created_at).toLocaleDateString('fr-FR') : '?'}</span>
                  <span>|</span>
                  <span>{ad.impressions_count || 0} impr.</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LeaderboardSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { at } = useAdminLang();

  useEffect(() => {
    axios.get('/api/admin/leaderboard').then(res => {
      setData(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-gray-400">{at('lbLoading')}</div>;
  if (!data) return null;

  const columns = [
    { key: 'top_followers', title: at('lbFollowers'), color: 'text-blue-400', icon: '👥' },
    { key: 'top_likes', title: at('lbLikes'), color: 'text-violet-400', icon: '❤️' },
    { key: 'top_posts', title: at('lbPosts'), color: 'text-magenta-400', icon: '📝' },
    { key: 'top_comments', title: at('lbComments'), color: 'text-green-400', icon: '💬' },
    { key: 'top_shares', title: at('lbShares'), color: 'text-orange-400', icon: '📤' },
    { key: 'top_referrers', title: at('lbReferrers'), color: 'text-emerald-400', icon: '🎁' },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">{at('leaderboardTitle')}</h2>
      {data.activity && !data.activity.error && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="admin-activity-stats">
          {[
            ['Discussions actives (30 j)', data.activity.active_conversations_30d, 'text-cyan-300'],
            ['Personnes qui discutent (30 j)', data.activity.active_talkers_30d, 'text-fuchsia-300'],
            ['Discussions au total', data.activity.total_conversations, 'text-gray-200'],
            ['Groupes (utilisateurs)', data.activity.total_groups, 'text-amber-300'],
            ['Groupes système (IA)', data.activity.system_groups, 'text-emerald-300'],
            ['Messages de groupe (30 j)', data.activity.group_messages_30d, 'text-violet-300'],
          ].map(([label, val, color]) => (
            <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-3">
              <p className="text-[11px] text-gray-400 leading-tight">{label}</p>
              <p className={`text-2xl font-black ${color}`}>{val ?? 0}</p>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {columns.map(col => (
          <div key={col.key} className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <h3 className={`text-sm font-bold mb-3 ${col.color} flex items-center gap-2`}>
              <span>{col.icon}</span> {col.title}
            </h3>
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {(data[col.key] || []).map((user, idx) => (
                <div key={user.id || user.username || idx} className="flex items-center gap-2 text-sm">
                  <span className={`w-5 text-center font-bold ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {idx + 1}
                  </span>
                  <span className="flex-1 truncate text-white">{user.name || user.username}</span>
                  <span className={`font-mono font-bold ${col.color}`}>{user.count || 0}</span>
                </div>
              ))}
              {(!data[col.key] || data[col.key].length === 0) && (
                <p className="text-xs text-gray-500">{at('lbEmpty')}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActionCard = ({ icon, title, description, onClick, highlight, ...rest }) => {
  return (
    <button
      onClick={onClick}
      data-testid={rest['data-testid']}
      className={`border rounded-xl p-6 hover:bg-white/10 transition-all text-left ${
        highlight 
          ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 hover:border-green-400' 
          : 'bg-white/5 border-white/10 hover:border-magenta-500/50'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className={`p-2 rounded-lg ${highlight ? 'bg-green-500/30' : 'bg-magenta-500/20'}`}>
          {icon}
        </div>
        <div>
          <h3 className="font-semibold mb-1">{title}</h3>
          <p className="text-sm text-gray-400">{description}</p>
        </div>
      </div>
    </button>
  );
};

// Badge Candidates Modal
const BadgeCandidatesModal = ({ onClose }) => {
  const [tab, setTab] = useState('influencers');
  const [data, setData] = useState({ influencers: [], pro_complete: [], threshold: 5000 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/badge-candidates');
      setData(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const grantBadge = async (userId) => {
    setUpdating(userId);
    try {
      await axios.post(`/api/admin/users/${userId}/toggle-verified`);
      toast.success('Badge attribué ✓');
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de l'attribution");
    } finally {
      setUpdating(null);
    }
  };

  const list = tab === 'influencers' ? data.influencers : data.pro_complete;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="badge-candidates-modal">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-yellow-400" />
            <h2 className="text-xl font-bold">Candidats au badge vérifié</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="close-badge-candidates">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-white/10">
          <button
            onClick={() => setTab('influencers')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${tab === 'influencers' ? 'text-magenta-400 border-b-2 border-magenta-500' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-influencers"
          >
            <Users className="w-4 h-4" />
            ≥{(data.threshold || 5000).toLocaleString()} abonnés ({data.influencers_count || 0})
          </button>
          <button
            onClick={() => setTab('pro')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${tab === 'pro' ? 'text-magenta-400 border-b-2 border-magenta-500' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-pro"
          >
            <Briefcase className="w-4 h-4" />
            Comptes PRO ({data.pro_complete_count || 0})
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Chargement...</div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              {tab === 'influencers'
                ? `Aucun utilisateur n'atteint ${(data.threshold || 5000).toLocaleString()} abonnés`
                : 'Aucun compte PRO complet'}
            </div>
          ) : (
            <div className="space-y-2">
              {list.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl" data-testid={`candidate-${u.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">@{u.username}</p>
                      {tab === 'influencers' ? (
                        <p className="text-xs text-magenta-400 mt-0.5">{(u.followers_count || 0).toLocaleString()} abonnés</p>
                      ) : (
                        <p className="text-xs text-cyan-400 mt-0.5">SIRET: {u.siret}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => grantBadge(u.id)}
                    disabled={updating === u.id}
                    className="px-3 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg text-sm font-medium disabled:opacity-50 flex-shrink-0 ml-2"
                    data-testid={`grant-badge-${u.id}`}
                  >
                    {updating === u.id ? '...' : '✓ Attribuer'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// AI Insights Modal: show GPT-generated analysis of app stats
const AIInsightsModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/ai-insights');
      setData(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInsights(); }, []);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="ai-insights-modal">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-bold">Déesse Insights</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchInsights} disabled={loading} className="p-2 hover:bg-white/10 rounded-full disabled:opacity-50" data-testid="refresh-ai-insights" title="Actualiser">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="close-ai-insights">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && !data && (
            <div className="text-center py-12 text-gray-400">
              <div className="animate-pulse">L&apos;IA analyse les données…</div>
            </div>
          )}
          {data && (
            <>
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-2xl p-4">
                <p className="text-xs uppercase font-semibold text-cyan-400 mb-2">📊 Analyse IA</p>
                <div className="text-sm leading-relaxed whitespace-pre-wrap text-gray-200" data-testid="ai-insights-analysis">
                  {data.analysis}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-2xl font-bold text-magenta-400">{data.stats.users_total}</p>
                  <p className="text-xs text-gray-400">Utilisateurs</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-2xl font-bold text-yellow-400">+{data.stats.new_users_24h}</p>
                  <p className="text-xs text-gray-400">Nouveaux 24h</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-2xl font-bold text-green-400">{data.stats.online_now}</p>
                  <p className="text-xs text-gray-400">En ligne maintenant</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-2xl font-bold text-cyan-400">{data.stats.gim_toli_24h}</p>
                  <p className="text-xs text-gray-400">Gim Toli (24h)</p>
                </div>
              </div>
              {Object.keys(data.stats.top_emotions_7d || {}).length > 0 && (
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-400 mb-2">Émotions dominantes (7j)</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.stats.top_emotions_7d).map(([k, v]) => (
                      <span key={k} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs">
                        {k}: <span className="font-bold text-magenta-300">{v}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.stats.top_creators?.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-400 mb-2">Top créateurs</p>
                  <div className="space-y-2">
                    {data.stats.top_creators.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-white/5 rounded-lg px-3 py-2">
                        <span>#{i + 1} {c.name}</span>
                        <span className="text-magenta-400 font-bold">{(c.followers || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[10px] text-gray-500 text-center">
                Généré le {new Date(data.generated_at).toLocaleString('fr-FR')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;

// API Integrations status modal
const ApiIntegrationsModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/admin/api-integrations')
      .then(res => setData(res.data))
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] flex items-center justify-center p-4" data-testid="api-integrations-modal" onClick={onClose}>
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plug className="w-6 h-6 text-green-400" />
            <h2 className="text-xl font-bold">API & Intégrations</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="close-api-integrations">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading && <p className="text-center text-gray-400 py-8">Chargement…</p>}
          {data && (
            <>
              {data.usage && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{data.usage.voice_stickers_sent}</p>
                    <p className="text-[10px] text-gray-400">Stickers vocaux</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-pink-400">{data.usage.voice_images_today}</p>
                    <p className="text-[10px] text-gray-400">Images IA (24h)</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-cyan-400">{data.usage.movie_games_total}</p>
                    <p className="text-[10px] text-gray-400">Parties cinéma</p>
                  </div>
                </div>
              )}
              {data.integrations.map(i => (
                <div key={i.id} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`integration-${i.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${i.configured ? 'bg-green-500' : 'bg-red-500'}`} />
                      <h4 className="font-semibold">{i.name}</h4>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${
                      i.configured ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                    }`}>
                      {i.configured ? 'Connecté' : 'Manquant'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mb-1">
                    <span className="font-medium text-gray-300">{i.category}</span>
                  </div>
                  <ul className="text-xs text-gray-400 list-disc list-inside space-y-0.5">
                    {i.uses.map((u, idx) => <li key={idx}>{u}</li>)}
                  </ul>
                </div>
              ))}
              <p className="text-[10px] text-gray-500 text-center">
                Vérifié le {new Date(data.checked_at).toLocaleString('fr-FR')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ===== Edit Admin PIN codes (super-admin only) =====
// Updating the PINs is a sensitive action: we require the user's account
// password AND date of birth as a second factor (mirrors how the backend
// gates POST /api/admin/pin-codes).
const EditPinCodesModal = ({ current, onClose, onSaved }) => {
  const [password, setPassword] = useState('');
  const [dob, setDob] = useState('');
  const [superPin, setSuperPin] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const clean = (v) => v.replace(/\D/g, '').slice(0, 4);

  const submit = async () => {
    setErr('');
    if (!password || !dob) { setErr('Mot de passe et date de naissance requis.'); return; }
    if (!superPin && !adminPin) { setErr('Saisissez au moins un nouveau code PIN.'); return; }
    if (superPin && superPin.length !== 4) { setErr('Le code Super Admin doit comporter 4 chiffres.'); return; }
    if (adminPin && adminPin.length !== 4) { setErr('Le code Admin doit comporter 4 chiffres.'); return; }
    setBusy(true);
    try {
      const body = { password, date_of_birth: dob };
      if (superPin) body.super_admin_pin = superPin;
      if (adminPin) body.admin_pin = adminPin;
      const r = await axios.post('/api/admin/pin-codes', body);
      onSaved?.({ super_admin_pin: r.data.super_admin_pin, admin_pin: r.data.admin_pin });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur — vérifiez le mot de passe / la date de naissance.');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="edit-pin-codes-modal"
    >
      <div
        className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-purple-400" /> Modifier les codes PIN</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <p className="text-xs text-gray-400">
            Action sensible — réservée au super admin. Renseignez votre mot de passe Destine + votre date de naissance, puis le ou les nouveaux PIN à 4 chiffres. Vous transmettrez vous-même les nouveaux codes aux autres administrateurs.
          </p>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Mot de passe (de votre compte)</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
              data-testid="edit-pin-password"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Date de naissance</label>
            <input
              type="date" value={dob} onChange={e => setDob(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
              data-testid="edit-pin-dob"
            />
          </div>

          <div className="pt-2 border-t border-white/10">
            <p className="text-xs text-gray-400 mb-2">Nouveaux codes PIN (laissez vide pour conserver l'actuel)</p>
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                Super Admin (actuel : <span className="font-mono">{current?.super_admin_pin || '—'}</span>)
              </label>
              <input
                value={superPin} onChange={e => setSuperPin(clean(e.target.value))}
                placeholder="•••• (4 chiffres)"
                inputMode="numeric"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono tracking-widest focus:outline-none focus:border-purple-500"
                data-testid="edit-pin-super"
              />
            </div>
            <div className="mt-2">
              <label className="text-xs text-gray-400 block mb-1">
                Autres Admins (actuel : <span className="font-mono">{current?.admin_pin || '—'}</span>)
              </label>
              <input
                value={adminPin} onChange={e => setAdminPin(clean(e.target.value))}
                placeholder="•••• (4 chiffres)"
                inputMode="numeric"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono tracking-widest focus:outline-none focus:border-purple-500"
                data-testid="edit-pin-admin"
              />
            </div>
          </div>

          {err && <p className="text-sm text-red-300" data-testid="edit-pin-error">{err}</p>}

          <button
            onClick={submit}
            disabled={busy}
            className="w-full py-2.5 rounded-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2"
            data-testid="edit-pin-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Enregistrer les nouveaux codes
          </button>
        </div>
      </div>
    </div>
  );
};


// ===== Destine IA — architecture panel (super-admin only) =====
// Calls /api/admin/destine-ia-architecture (403 for non-super-admin) and
// renders a readable summary of the IA stack so the founder can quickly
// answer: "C'est codé en quoi ? Puis-je la réutiliser ailleurs ?"

// ===== Destine Team — super-admin only =====
const DestineTeamSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="destine-team-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
        data-testid="team-section-toggle"
      >
        <Users className="w-4 h-4 text-emerald-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : (at('teamSectionTitle') || 'Équipe — Admins, Managers, Partenaires')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
          <AdminTeamPanel />
        </div>
      )}
    </div>
  );
};


// ===== Démarches administratives — super-admin + managers (v50.8) =====
const BusinessTasksSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="business-tasks-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-rose-500/20 rounded-xl text-sm transition-colors w-full"
        data-testid="business-tasks-section-toggle"
      >
        <Briefcase className="w-4 h-4 text-rose-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : at('bizTasksSectionTitle')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-rose-500/5 border border-rose-500/20 rounded-xl p-4">
          <BusinessTasksPanel />
        </div>
      )}
    </div>
  );
};


// ===== Green IA — dedicated top-level admin section (v50.15) =====
const GreenIAAdminSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="green-ia-admin-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-green-500/20 rounded-xl text-sm transition-colors w-full"
        data-testid="green-ia-section-toggle"
      >
        <Shield className="w-4 h-4 text-green-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : (at('greenIaSectionTitle') || 'Green IA')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
          <GreenIARegistryAdminPanel />
          <GreenIADivestPanel />
        </div>
      )}
    </div>
  );
};


// ===== Abonnements — dedicated top-level admin section (v50.15) =====
const SubscriptionsAdminSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="subscriptions-admin-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-yellow-500/20 rounded-xl text-sm transition-colors w-full"
        data-testid="subscriptions-section-toggle"
      >
        <Euro className="w-4 h-4 text-yellow-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : (at('subscriptionsSectionTitle') || 'Abonnements')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4">
          <SubscriptionsAdminPanel />
        </div>
      )}
    </div>
  );
};


// ===== Abonnements Premium Ads — Ads le commercial (v60.2) =====
const AdsPremiumAdminSection = () => {
  const [show, setShow] = useState(false);
  const { at, adminLang } = useAdminLang();
  const title = { fr: 'Abonnements Premium Ads', en: 'Premium Ads subscriptions', es: 'Suscripciones Premium Ads', zh: 'Premium Ads 订阅' }[adminLang] || 'Abonnements Premium Ads';
  return (
    <div data-testid="ads-premium-admin-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-fuchsia-500/20 rounded-xl text-sm transition-colors w-full"
        data-testid="ads-premium-section-toggle"
      >
        <Bot className="w-4 h-4 text-fuchsia-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : title}</span>
      </button>
      {show && (
        <div className="mt-3 bg-fuchsia-500/5 border border-fuchsia-500/20 rounded-xl p-4">
          <AdsPremiumAdminPanel />
        </div>
      )}
    </div>
  );
};


// ===== Notes utilisateurs (sorties & box office) — dedicated section (v50.16) =====
const CinemaRatingsAdminSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="cinema-ratings-admin-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-amber-500/20 rounded-xl text-sm transition-colors w-full"
        data-testid="cinema-ratings-section-toggle"
      >
        <Star className="w-4 h-4 text-amber-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : (at('ratingsSectionTitle') || 'Notes utilisateurs — Sorties & Box-office')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
          <CinemaRatingsPanel />
        </div>
      )}
    </div>
  );
};


// ===== Agents IA Social Media — dedicated section (v50.16) =====
const SocialAgentsAdminSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="social-agents-admin-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-sky-500/20 rounded-xl text-sm transition-colors w-full"
        data-testid="social-agents-section-toggle"
      >
        <Share2 className="w-4 h-4 text-sky-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : (at('socialAgentsSectionTitle') || 'Agents IA Social Media')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-sky-500/5 border border-sky-500/20 rounded-xl p-4">
          <SocialAIAgentsPanel />
          <SocialConnectPublish />
          <AdminVideoStudio />
        </div>
      )}
    </div>
  );
};


// ===== Security — super-admin + managers =====
const DestineSecuritySection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="destine-security-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
        data-testid="security-section-toggle"
      >
        <Shield className="w-4 h-4 text-indigo-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : at('securityTitle')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4">
          <AdminSecurityPanel />
          <CyberdefensePanel />
        </div>
      )}
    </div>
  );
};


// ===== Regional-language corpus — super-admin + managers =====
const DestineRegionalSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="destine-regional-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
        data-testid="regional-section-toggle"
      >
        <Languages className="w-4 h-4 text-emerald-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : at('regionalTitle')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
          <AdminRegionalPanel />
        </div>
      )}
    </div>
  );
};

// ===== GPT Africa corpus (v50.10.8c) — just below the Guyane section =====
const DestineAfricaSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="destine-africa-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
        data-testid="africa-section-toggle"
      >
        <Languages className="w-4 h-4 text-amber-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : at('africaTitle')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
          <AdminRegionalPanel region="africa" />
        </div>
      )}
    </div>
  );
};


// ===== Destine AP-IA — API keys management (super-admin only) =====
const DestineApiaKeysSection = () => {
  const [show, setShow] = useState(false);
  const { at } = useAdminLang();
  return (
    <div data-testid="destine-apia-section">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
        data-testid="apia-section-toggle"
      >
        <Sparkles className="w-4 h-4 text-indigo-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : at('tabApia')}</span>
      </button>
      {show && (
        <div className="mt-3 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4">
          <AdminApiaPanel />
        </div>
      )}
    </div>
  );
};


const DestineIaArchitectureSection = () => {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { at } = useAdminLang();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/destine-ia-architecture');
      setData(res.data);
      setShow(true);
    } catch {
      setShow(false);
      toast.error("Accès réservé au super admin");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="destine-ia-architecture-section">
      <button
        onClick={() => show ? setShow(false) : load()}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
        data-testid="ia-architecture-toggle"
      >
        <Brain className="w-4 h-4 text-fuchsia-400" />
        <span className="flex-1 text-left">{show ? at('hideSection') : at('iaArchitectureSection')}</span>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      </button>
      {show && data && (
        <div className="mt-3 bg-fuchsia-500/5 border border-fuchsia-500/20 rounded-xl p-4 space-y-4" data-testid="ia-architecture-content">
          <DestineIAAvatar />
          <DeesseEmotionalPanel />
          <BookScanner />
          <div>
            <p className="text-base font-bold text-fuchsia-300 flex items-center gap-2">
              <Brain className="w-4 h-4" /> {data.name}
            </p>
            <p className="text-xs text-gray-300 mt-1">{data.tagline}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-fuchsia-200 mb-1 flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5" /> Stack technique</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              {Object.entries(data.stack).map(([k, v]) => (
                <div key={k}>
                  <span className="text-gray-400">{k.replace(/_/g, ' ')} :</span>{' '}
                  <span className="text-white">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-fuchsia-200 mb-1">Fournisseurs IA</p>
            <ul className="space-y-1 text-xs">
              {data.ai_providers.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-gray-400 w-32 shrink-0">{p.role}</span>
                  <span className="text-white"><span className="font-semibold">{p.model}</span> <span className="text-gray-500">— via {p.via}</span></span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold text-fuchsia-200 mb-1 flex items-center gap-1.5"><Code className="w-3.5 h-3.5" /> Fichiers principaux</p>
            <ul className="space-y-1 text-xs font-mono">
              {data.main_code_files.map((f, i) => (
                <li key={i}>
                  <span className="text-fuchsia-200">{f.path}</span>
                  <p className="text-gray-400 font-sans text-[11px] ml-1">{f.purpose}</p>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold text-fuchsia-200 mb-1">Endpoints REST</p>
            <ul className="space-y-1 text-xs font-mono">
              {data.key_endpoints.map((e, i) => (
                <li key={i}>
                  <span className="text-emerald-300">{e.method}</span> <span className="text-white">{e.path}</span>
                  <span className="text-gray-400 font-sans ml-1">— {e.purpose}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-xs font-semibold text-emerald-200 mb-1">♻️ Portabilité — réutilisation dans une autre app</p>
            <p className="text-xs text-gray-200 mb-2">{data.reuse_instructions.summary}</p>
            <ol className="space-y-1 text-xs text-gray-200 list-none">
              {data.reuse_instructions.steps.map((s, i) => (
                <li key={i} className="pl-2"><span className="text-emerald-300 font-mono">{i + 1}.</span> {s.replace(/^\d+\.\s*/, '')}</li>
              ))}
            </ol>
            <p className="text-[11px] text-amber-300 mt-2">Limitations :</p>
            <ul className="text-[11px] text-gray-300 list-disc ml-4">
              {data.reuse_instructions.constraints.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>

          <p className="text-[10px] text-gray-500 text-center">{data.license}</p>
          <p className="text-[10px] text-gray-500 text-center">Vérifié le {new Date(data.checked_at).toLocaleString('fr-FR')}</p>

          {/* v50.16 — panels moved to their dedicated sections (Actions rapides / Comptabilité) */}
        </div>
      )}
    </div>
  );
};



// ===== Anti-abuse dashboard (rate-limit / paid-follower detection) =====
const AntiAbuseSection = () => {
  const [show, setShow] = useState(false);
  const [tab, setTab] = useState('top');
  const [topData, setTopData] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadTop = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/abuse/top-offenders', { params: { days: 7, limit: 20 } });
      setTopData(r.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };
  const loadEvents = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/abuse/events', { params: { limit: 50 } });
      setEvents(r.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };
  const loadUser = async (uid) => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/admin/abuse/user/${uid}`);
      setSelectedUser(r.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };
  const suspend = async (uid) => {
    if (!window.confirm('Suspendre ce compte pour abus ?')) return;
    try {
      await axios.post(`/api/admin/abuse/user/${uid}/suspend`, { reason: 'Suspicion of paid engagement' });
      toast.success('Compte suspendu');
      loadUser(uid); loadTop();
    } catch (e) { toast.error("Erreur"); }
  };
  const unsuspend = async (uid) => {
    try {
      await axios.post(`/api/admin/abuse/user/${uid}/unsuspend`);
      toast.success('Suspension levée');
      loadUser(uid); loadTop();
    } catch (e) { toast.error("Erreur"); }
  };

  useEffect(() => {
    if (!show) return;
    if (tab === 'top') loadTop(); else if (tab === 'events') loadEvents();
  }, [show, tab]);

  return (
    <div data-testid="anti-abuse-section" className="mt-6">
      <button
        onClick={() => setShow(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm transition-colors w-full"
      >
        <Shield className="w-4 h-4 text-red-400" />
        <span className="flex-1 text-left">{show ? '▼ Anti-abus (fake likes / abonnés)' : '▶ Anti-abus (fake likes / abonnés)'}</span>
      </button>
      {show && (
        <div className="mt-3 bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setTab('top')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === 'top' ? 'bg-red-500 text-white' : 'bg-white/5'}`} data-testid="abuse-tab-top">Top offenders</button>
            <button onClick={() => setTab('events')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === 'events' ? 'bg-red-500 text-white' : 'bg-white/5'}`} data-testid="abuse-tab-events">Events</button>
          </div>

          {loading && <p className="text-gray-400 text-sm">Chargement…</p>}

          {tab === 'top' && topData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 mb-2">Top utilisateurs ({topData.days}j)</p>
                <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
                  {(topData.top_users || []).length === 0 && <p className="text-xs text-gray-500">Aucune activité suspecte</p>}
                  {(topData.top_users || []).map((u) => (
                    <button key={u.user_id} onClick={() => loadUser(u.user_id)} className="w-full flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-left text-xs">
                      <span className="font-mono text-red-300 w-8 text-right">{u.count}</span>
                      <span className="flex-1 truncate">{u.name || u.username || u.user_id.slice(0, 8)}</span>
                      {u.suspended && <span className="text-xs bg-red-500/30 px-2 rounded">Susp.</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 mb-2">Top IPs ({topData.days}j)</p>
                <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
                  {(topData.top_ips || []).length === 0 && <p className="text-xs text-gray-500">Aucune IP suspecte</p>}
                  {(topData.top_ips || []).map((r) => (
                    <div key={r.ip} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 text-xs">
                      <span className="font-mono text-orange-300 w-8 text-right">{r.count}</span>
                      <span className="flex-1 truncate font-mono">{r.ip}</span>
                      <span className="text-gray-400">{r.user_count} user(s)</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'events' && events && (
            <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1">
              <p className="text-xs text-gray-400 mb-2">{events.total} événements enregistrés</p>
              {(events.events || []).map((e, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 text-xs">
                  <span className="text-gray-500 w-40 truncate">{(e.at || '').replace('T', ' ').slice(0, 19)}</span>
                  <span className="w-32 truncate font-semibold text-red-300">{e.action}</span>
                  <span className="flex-1 truncate text-gray-300">{e.reason}</span>
                  <button onClick={() => loadUser(e.user_id)} className="text-cyan-300 hover:underline">→ user</button>
                </div>
              ))}
            </div>
          )}

          {selectedUser && (
            <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg">{selectedUser.user.name}</h3>
                  <p className="text-xs text-gray-400">@{selectedUser.user.username} · {selectedUser.user.email}</p>
                </div>
                <button onClick={() => setSelectedUser(null)} className="p-1 hover:bg-white/10 rounded-full"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                <div className="p-2 rounded bg-white/5 text-center"><p className="text-gray-400">Score</p><p className={`font-bold text-lg ${selectedUser.risk.level === 'critical' ? 'text-red-500' : selectedUser.risk.level === 'high' ? 'text-orange-400' : selectedUser.risk.level === 'medium' ? 'text-yellow-400' : 'text-green-400'}`}>{selectedUser.risk.score}</p><p className="text-[10px] uppercase">{selectedUser.risk.level}</p></div>
                <div className="p-2 rounded bg-white/5 text-center"><p className="text-gray-400">Events 24h</p><p className="font-bold text-lg">{selectedUser.risk.events_24h}</p></div>
                <div className="p-2 rounded bg-white/5 text-center"><p className="text-gray-400">Events 7j</p><p className="font-bold text-lg">{selectedUser.risk.events_7d}</p></div>
                <div className="p-2 rounded bg-white/5 text-center"><p className="text-gray-400">Follow/Unfollow</p><p className="font-bold text-lg text-orange-400">{selectedUser.risk.follow_unfollow_7d}</p></div>
              </div>
              {selectedUser.user.suspended ? (
                <button onClick={() => unsuspend(selectedUser.user.id)} className="w-full px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-full text-sm font-semibold text-emerald-300" data-testid="abuse-unsuspend-btn">Lever la suspension</button>
              ) : (
                <button onClick={() => suspend(selectedUser.user.id)} className="w-full px-3 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-full text-sm font-semibold text-red-300" data-testid="abuse-suspend-btn">Suspendre le compte</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// UsersByLanguageModal — shows user distribution across the 13 supported
// languages. Opened by clicking the "Utilisateurs" StatCard on the dashboard.
// ============================================================================
const LANG_LABELS = {
  fr: { fr: 'Français',  en: 'French',      es: 'Francés',   zh: '法语' },
  en: { fr: 'Anglais',   en: 'English',     es: 'Inglés',    zh: '英语' },
  es: { fr: 'Espagnol',  en: 'Spanish',     es: 'Español',   zh: '西班牙语' },
  ar: { fr: 'Arabe',     en: 'Arabic',      es: 'Árabe',     zh: '阿拉伯语' },
  pt: { fr: 'Portugais', en: 'Portuguese',  es: 'Portugués', zh: '葡萄牙语' },
  de: { fr: 'Allemand',  en: 'German',      es: 'Alemán',    zh: '德语' },
  ru: { fr: 'Russe',     en: 'Russian',     es: 'Ruso',      zh: '俄语' },
  zh: { fr: 'Chinois',   en: 'Chinese',     es: 'Chino',     zh: '中文' },
  ja: { fr: 'Japonais',  en: 'Japanese',    es: 'Japonés',   zh: '日语' },
  hi: { fr: 'Hindi',     en: 'Hindi',       es: 'Hindi',     zh: '印地语' },
  nl: { fr: 'Néerlandais', en: 'Dutch',     es: 'Neerlandés', zh: '荷兰语' },
  sw: { fr: 'Swahili',   en: 'Swahili',     es: 'Suajili',   zh: '斯瓦希里语' },
  ht: { fr: 'Créole haïtien', en: 'Haitian Creole', es: 'Criollo haitiano', zh: '海地克里奥尔语' },
};
const LANG_FLAGS = { fr: '🇫🇷', en: '🇬🇧', es: '🇪🇸', ar: '🇸🇦', pt: '🇵🇹', de: '🇩🇪', ru: '🇷🇺', zh: '🇨🇳', ja: '🇯🇵', hi: '🇮🇳', nl: '🇳🇱', sw: '🇰🇪', ht: '🇭🇹' };

const UsersByLanguageModal = ({ onClose }) => {
  const { at, adminLang } = useAdminLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/admin/users-by-language');
        setData(res.data || null);
      } catch (e) {
        console.error('users-by-language load failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const supported = data?.supported || ['fr', 'en', 'es', 'ar', 'pt', 'de', 'ru', 'zh', 'ja', 'hi', 'nl', 'sw', 'ht'];
  const total = data?.total || 0;
  const rows = supported
    .map((code) => ({ code, count: data?.by_language?.[code] || 0 }))
    .sort((a, b) => b.count - a.count);
  const maxCount = Math.max(1, ...rows.map((r) => r.count));

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="users-by-lang-modal">
      <div className="bg-gray-900 rounded-2xl border border-white/10 p-6 max-w-2xl w-full max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2" data-testid="users-by-lang-title">
              <Globe className="w-5 h-5" /> {at('usersByLangTitle') || 'Utilisateurs par langue'}
            </h2>
            <p className="text-xs text-gray-400 mt-1">{at('usersByLangSubtitle')}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="users-by-lang-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : total === 0 ? (
          <p className="text-center py-8 text-gray-400">{at('usersByLangEmpty')}</p>
        ) : (
          <>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-4 flex items-center justify-between">
              <span className="text-sm text-gray-300">{at('usersByLangTotal')}</span>
              <span className="text-2xl font-bold" data-testid="users-by-lang-total">{total.toLocaleString()}</span>
            </div>
            <ul className="space-y-2">
              {rows.map((r) => {
                const label = LANG_LABELS[r.code]?.[adminLang] || LANG_LABELS[r.code]?.en || r.code;
                const pct = total > 0 ? (r.count / total) * 100 : 0;
                const w = (r.count / maxCount) * 100;
                return (
                  <li key={r.code} data-testid={`users-by-lang-row-${r.code}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{LANG_FLAGS[r.code] || '🏳️'}</span>
                        <span className="font-medium">{label}</span>
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-mono">{r.code}</span>
                      </span>
                      <span className="tabular-nums">
                        <span className="font-semibold">{r.count.toLocaleString()}</span>
                        <span className="text-gray-500 text-xs ml-2">{pct.toFixed(1)}%</span>
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all" style={{ width: `${w}%` }} />
                    </div>
                  </li>
                );
              })}
              {(data?.other || 0) > 0 && (
                <li className="pt-2 border-t border-white/10 mt-2" data-testid="users-by-lang-row-other">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{at('usersByLangOther')}</span>
                    <span className="tabular-nums text-gray-400">{(data.other).toLocaleString()}</span>
                  </div>
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
